import { useParams, useNavigate } from 'react-router-dom';
import { insightsData } from '../../mocks/insights';

export default function PostDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const post = insightsData.find(p => p.id === Number(id));

  if (!post) {
    return (
      <div className="min-h-screen bg-[#f5f1e8] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl text-[#4a3f33] mb-4">게시글을 찾을 수 없습니다</h1>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#8b4513] text-white rounded-lg hover:bg-[#654321] transition-colors cursor-pointer whitespace-nowrap"
          >
            홈으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  const categoryConfig: Record<string, { name: string; color: string; icon: string }> = {
    'core-loop': { name: '히트 코어루프', color: '#8b4513', icon: 'ri-fire-fill' },
    'fast-follower': { name: '패스트 팔로워', color: '#2f4f4f', icon: 'ri-rocket-fill' },
    'idea-archive': { name: '해든의 코어루프 아이디어 아카이빙', color: '#5a4a3a', icon: 'ri-gamepad-fill' },
    'hit-prediction': { name: '히트 가능성 예측 리포트', color: '#4a3a5a', icon: 'ri-line-chart-fill' }
  };

  const currentCategory = categoryConfig[post.category];

  const renderMarkdown = (content: string) => {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    let currentList: string[] = [];
    let key = 0;

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <ul key={`list-${key++}`} className="space-y-2 my-4">
            {currentList.map((item, idx) => (
              <li key={idx} className="flex items-start">
                <span className="mr-2">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
        currentList = [];
      }
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        flushList();
        return;
      }

      // Headers
      if (trimmedLine.startsWith('### ')) {
        flushList();
        elements.push(
          <h3 key={`h3-${key++}`} className="text-xl font-bold mt-8 mb-4">
            {trimmedLine.substring(4)}
          </h3>
        );
      } else if (trimmedLine.startsWith('## ')) {
        flushList();
        elements.push(
          <h2 key={`h2-${key++}`} className="text-2xl font-bold mt-10 mb-4">
            {trimmedLine.substring(3)}
          </h2>
        );
      } else if (trimmedLine.startsWith('# ')) {
        flushList();
        elements.push(
          <h1 key={`h1-${key++}`} className="text-3xl font-bold mt-12 mb-6">
            {trimmedLine.substring(2)}
          </h1>
        );
      }
      // Blockquote
      else if (trimmedLine.startsWith('> ')) {
        flushList();
        elements.push(
          <blockquote key={`quote-${key++}`} className="border-l-4 border-gray-300 pl-4 italic my-4 text-gray-600">
            {trimmedLine.substring(2)}
          </blockquote>
        );
      }
      // List items
      else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
        currentList.push(trimmedLine.substring(2));
      }
      // Regular paragraph
      else {
        flushList();
        // Handle bold text
        const parts = trimmedLine.split(/(\*\*.*?\*\*)/g);
        const content = parts.map((part, idx) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={idx}>{part.slice(2, -2)}</strong>;
          }
          return part;
        });
        
        elements.push(
          <p key={`p-${key++}`} className="my-4 leading-relaxed">
            {content}
          </p>
        );
      }
    });

    flushList();
    return elements;
  };

  const contentElements = renderMarkdown(post.content);

  return (
    <div className="min-h-screen bg-[#f5f1e8]">
      {/* Header */}
      <div className="bg-white/60 border-b border-[#e8dfd0] sticky top-0 z-40 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(`/board/${post.category}`)}
            className="flex items-center gap-2 text-[#6b5d4f] hover:text-[#4a3f33] transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl"></i>
            <span className="text-sm">목록으로</span>
          </button>
          <div className="flex items-center gap-2 text-sm" style={{ color: currentCategory.color }}>
            <i className={`${currentCategory.icon} text-lg`}></i>
            <span>{currentCategory.name}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-6 py-12">
        {/* Post Header */}
        <header className="mb-12 pb-8 border-b border-[#e8dfd0]">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm text-[#9d8b6f]">{post.date}</span>
          </div>
          <h1 
            className="text-4xl font-light mb-6 leading-relaxed"
            style={{ 
              fontFamily: 'Georgia, serif',
              color: currentCategory.color
            }}
          >
            {post.title}
          </h1>
          <p className="text-lg text-[#6b5d4f] leading-relaxed">
            {post.excerpt}
          </p>
        </header>

        {/* Post Content */}
        <div className="space-y-4">
          {contentElements}
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-[#e8dfd0]">
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-white/60 border border-[#e8dfd0] rounded-full text-sm text-[#6b5d4f]"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Links Section for Idea Archive */}
        {post.category === 'idea-archive' && (post.videoUrl || post.gameLink) && (
          <div className="mt-12 pt-8 border-t border-[#e8dfd0]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {post.videoUrl && (
                <a
                  href={post.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 p-6 bg-gradient-to-br from-[#8b4513] to-[#654321] rounded-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <i className="ri-play-circle-fill text-3xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <div className="text-white/80 text-sm mb-1">영상 보기</div>
                    <div className="text-white font-light">프로토타입 영상</div>
                  </div>
                  <i className="ri-external-link-line text-white/60 text-xl group-hover:text-white transition-colors"></i>
                </a>
              )}
              {post.gameLink && (
                <a
                  href={post.gameLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 p-6 bg-gradient-to-br from-[#2f4f4f] to-[#1f3f3f] rounded-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <i className="ri-gamepad-fill text-3xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <div className="text-white/80 text-sm mb-1">게임 플레이</div>
                    <div className="text-white font-light">직접 플레이하기</div>
                  </div>
                  <i className="ri-external-link-line text-white/60 text-xl group-hover:text-white transition-colors"></i>
                </a>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="mt-16 pt-8 border-t border-[#e8dfd0] flex justify-between items-center">
          <button
            onClick={() => navigate(`/board/${post.category}`)}
            className="px-6 py-3 bg-white/60 border border-[#e8dfd0] rounded-lg text-[#6b5d4f] hover:bg-white hover:border-[#d4c4a8] transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            목록으로
          </button>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#8b4513] text-white rounded-lg hover:bg-[#654321] transition-colors cursor-pointer whitespace-nowrap"
          >
            홈으로
          </button>
        </div>
      </article>
    </div>
  );
}
